import { Ride, RideService } from '@geovelo-frontends/commons';
import { Box, ButtonBase, Skeleton, Typography, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Link, graphql, navigate } from 'gatsby';
import { GatsbyImage, getImage } from 'gatsby-plugin-image';
import { withPrismicPreview } from 'gatsby-plugin-prismic-previews';
import moment from 'moment';
import React, { Fragment, useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { styled } from 'styled-components';

import { BlogPostQuery } from '../../../graphql-types';
import Button from '../../components/button';
import RideRecap from '../../components/ride-recap';
import Seo from '../../components/seo';
import CTA from '../../components/slices/cta';
import Description from '../../components/slices/description';
import Title from '../../components/slices/title';
import { TPageProps } from '../../page-props';
import BlogPostCard from '../blog/blog-post-card';
import Breadcrumb from '../blog/breadcrumb';

import ShareButtons from './share-buttons';

function BlogPostPage(props: TPageProps<BlogPostQuery>) {
  const {
    data: {
      prismicBlog,
      allPrismicBlogPostTheme: { nodes: allThemes },
      allPrismicAuthor: { nodes: allAuthors },
      prismicBlogPost,
    },
  } = props;
  const [rides, setRides] = useState<Ride[] | undefined | null>();
  const {
    t,
    i18n: { language: currentLanguage },
  } = useTranslation();
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down('md'));

  useEffect(() => {
    if (process.env.GATSBY_BLOG_DISABLED === 'true') navigate(`/`);
    else if (!prismicBlogPost) navigate(`/blog`);
  }, []);

  if (!prismicBlogPost || process.env.GATSBY_BLOG_DISABLED === 'true') return <></>;

  const {
    first_publication_date,
    data: {
      themes: _themes,
      author: _author,
      title,
      meta_title,
      publication_date,
      image: _image,
      subtitle,
      subtitle_short,
      sections,
      related_blog_posts,
    },
  } = prismicBlogPost;
  const relatedBlogPosts = related_blog_posts.filter(({ related_blog_post }) =>
    Boolean(related_blog_post),
  );
  const date = moment(publication_date || first_publication_date);
  const image = _image && getImage(_image);

  const themes = _themes
    ?.map((data) => allThemes.find(({ uid }) => uid === data?.theme?.uid)?.data.title?.text)
    .filter(Boolean) as string[] | undefined;
  const author = allAuthors.find(({ _previewable }) => _previewable === _author?.id)?.data;
  const authorImage = author?.author_photo && getImage(author.author_photo);

  useEffect(() => {
    if (sections) {
      const ids = sections.reduce<number[]>((res, { recap_ride_id }) => {
        if (recap_ride_id) res.push(recap_ride_id);
        return res;
      }, []);
      getRides(ids);
    }
  }, [sections]);

  async function getRides(ids: number[]) {
    if (ids.length === 0) return;
    try {
      const result = await Promise.all(ids.map((id) => RideService.getRide(id)));

      setRides(result);
    } catch (err) {
      if (
        !(err instanceof Error) ||
        err?.name !== 'CancelledPromiseError' ||
        (typeof err === 'object' && err && 'detail' in err)
      ) {
        console.error(err);
        setRides(null);
      }
    }
  }

  return (
    <>
      <Seo
        description={
          subtitle_short?.text
            ? subtitle_short.text.length > 300
              ? `${subtitle_short.text.substring(0, 297)}...`
              : subtitle_short.text
            : null
        }
        imageUrl={_image?.url}
        ld={{
          '@context': 'https://schema.org',
          '@type': 'NewsArticle',
          headline: title.text,
          image: _image?.url,
          datePublished: date.toISOString(),
          author: [
            {
              '@type': 'Organization',
              name: 'Geovelo',
              url: 'https://geovelo.app/blog',
            },
          ],
        }}
        metaTitle={meta_title?.text || title?.text || ''}
        title={title?.text || ''}
        {...props}
      />
      <Box alignItems="center" display="flex" flexDirection="column">
        <Box
          display="flex"
          flexDirection="column"
          gap={8}
          maxWidth="100%"
          paddingX={{ xs: 4, md: 8, lg: 16 }}
          paddingY={{ xs: 6, lg: 8 }}
          width={1440}
        >
          <Box display="flex" flexDirection="column" gap={2}>
            <Breadcrumb items={[{ label: title.text || '' }]} />
            <Title component="h1" data={title} />
            {date && (
              <Typography color="textSecondary" variant="body2">
                {date?.format('L')}
              </Typography>
            )}
          </Box>
          <Box display="flex" flexDirection={{ xs: 'column-reverse', md: 'row' }} gap={5}>
            <Box display="flex" flexDirection="column" flexGrow={1} gap={8}>
              <Box display="flex" flexDirection="column" gap={2}>
                <Box borderRadius={6} overflow="hidden">
                  {image && (
                    <Box title={_image?.alt || ''}>
                      <GatsbyImage alt={_image?.alt || ''} image={image} />
                    </Box>
                  )}
                </Box>
                {_image?.copyright && (
                  <Typography color="textSecondary" variant="caption">
                    ©{_image.copyright}
                  </Typography>
                )}
              </Box>
              <Box alignSelf="center" display="flex" flexDirection="column" gap={5} maxWidth="100%">
                {subtitle?.html && (
                  <Description
                    data={subtitle}
                    display="flex"
                    flexDirection="column"
                    fontSize="1em"
                    gap="0.5em"
                    linkColor={theme.palette.primary.main}
                  />
                )}
                {sections?.map((section, index) => {
                  if (!section) return <Fragment key={index} />;

                  const {
                    image_order,
                    ride_order,
                    text_order,
                    insert_order,
                    iframe_order,
                    cta_order,
                    section_title,
                    section_text,
                    section_iframe_link,
                    section_iframe_height,
                    section_image,
                    section_insert,
                    section_cta_link,
                    section_cta_title,
                    recap_cta_title,
                    recap_cta_link,
                    recap_ride_id,
                  } = section;
                  const sectionImage = section_image && getImage(section_image);
                  const order = ['image', 'ride', 'text', 'insert', 'iframe', 'cta'];
                  if (image_order) order[image_order - 1] = 'image';
                  if (ride_order) order[ride_order - 1] = 'ride';
                  if (text_order) order[text_order - 1] = 'text';
                  if (insert_order) order[insert_order - 1] = 'insert';
                  if (iframe_order) order[iframe_order - 1] = 'iframe';
                  if (cta_order) order[cta_order - 1] = 'cta';

                  return (
                    <Box
                      display="flex"
                      flexDirection="column"
                      gap={2}
                      id={section_title.text || ''}
                      key={index}
                      sx={{ scrollMarginTop: 80 }}
                    >
                      {section_title?.text && (
                        <Title
                          color="primary"
                          component="h2"
                          data={section_title}
                          fontSize="1.5em"
                          marginBottom={2}
                        />
                      )}
                      {order.map((element) => {
                        if (element === 'image')
                          return (
                            <>
                              {sectionImage && (
                                <Box display="flex" flexDirection="column" gap={2}>
                                  <Box alignSelf="center" title={section_image.alt || ''}>
                                    <GatsbyImage
                                      alt={section_image.alt || ''}
                                      image={sectionImage}
                                      style={{ maxWidth: section_image.dimensions?.width }}
                                    />
                                  </Box>
                                  {section_image?.copyright && (
                                    <Typography color="textSecondary" variant="caption">
                                      ©{section_image.copyright}
                                    </Typography>
                                  )}
                                </Box>
                              )}
                            </>
                          );
                        else if (element === 'ride')
                          return (
                            <>
                              {rides && recap_ride_id && (
                                <RideRecap
                                  disableGPXDownload
                                  cta_link={recap_cta_link}
                                  cta_title={recap_cta_title}
                                  ride={rides.find(({ id }) => recap_ride_id === id)}
                                />
                              )}
                            </>
                          );
                        else if (element === 'text')
                          return (
                            <>
                              {section_text?.html && (
                                <Description
                                  data={section_text}
                                  display="flex"
                                  flexDirection="column"
                                  fontSize="1em"
                                  gap="1.125em"
                                  linkColor={theme.palette.primary.main}
                                />
                              )}
                            </>
                          );
                        else if (element === 'insert')
                          return (
                            <>
                              {section_insert?.html && (
                                <Box
                                  sx={{ backgroundColor: '#C2D5F3', borderRadius: 2, padding: 2 }}
                                >
                                  <Description
                                    data={section_insert}
                                    display="flex"
                                    flexDirection="column"
                                    fontSize="1em"
                                    gap="1.125em"
                                    linkColor={theme.palette.primary.main}
                                  />
                                </Box>
                              )}
                            </>
                          );
                        else if (element === 'iframe')
                          return (
                            <>
                              {section_iframe_link &&
                                section_iframe_link.link_type === 'Web' &&
                                section_iframe_link.url && (
                                  <iframe
                                    seamless
                                    height={section_iframe_height || undefined}
                                    src={section_iframe_link.url}
                                    style={{ border: 0 }}
                                  />
                                )}
                            </>
                          );
                        else if (element === 'cta')
                          return (
                            <>
                              {section_cta_title?.text && (
                                <Box display="flex" justifyContent="center" marginTop={2}>
                                  <CTA
                                    color="secondary"
                                    link={section_cta_link}
                                    size="small"
                                    title={section_cta_title}
                                  />
                                </Box>
                              )}
                            </>
                          );
                      })}
                    </Box>
                  );
                })}
                {author && (
                  <Box
                    borderTop="1px solid #eeeeee"
                    display="flex"
                    gap={3}
                    padding="16px 16px 0 16px"
                  >
                    <Box borderRadius="100%" height={80} overflow="hidden" width={80}>
                      {authorImage ? (
                        <GatsbyImage
                          alt=""
                          image={authorImage}
                          objectFit="cover"
                          style={{ height: '100%', width: '100%' }}
                        />
                      ) : (
                        <Skeleton height="100%" variant="rectangular" width="100%" />
                      )}
                    </Box>
                    <Box display="flex" flexDirection="column" justifyContent="center">
                      <Description
                        color="primary"
                        data={author.author_name}
                        display="flex"
                        flexDirection="column"
                        fontSize="1.25em"
                        fontWeight={600}
                        gap="1.125em"
                        linkColor={theme.palette.primary.main}
                      />
                      <Description
                        data={author.author_role}
                        display="flex"
                        flexDirection="column"
                        fontSize="1em"
                        gap="1.125em"
                        linkColor={theme.palette.primary.main}
                      />
                    </Box>
                  </Box>
                )}
                {isSmall && (
                  <ShareButtons
                    summary={subtitle?.text || undefined}
                    themes={themes}
                    title={`Article Geovelo - ${title?.text}`}
                    url={props.location.href}
                  />
                )}
              </Box>
            </Box>
            <Box display="flex" flexShrink={0} position="relative" width={300}>
              <Box
                borderLeft={{ xs: 'none', md: '1px solid #ccc' }}
                display="flex"
                flexDirection="column"
                gap={1}
                height="fit-content"
                marginTop={{ xs: -3, md: 0 }}
                paddingLeft={3}
                position={{ xs: 'inherit', md: 'sticky' }}
                top={{ xs: 0, md: 200 }}
                width="inherit"
              >
                <Typography color="primary" component="h2" fontSize="1.125rem">
                  <Trans i18nKey="geovelo.blog_post.summary" />
                </Typography>
                {sections.map(({ section_title, section_summary_title }) => {
                  if (section_title?.text || section_summary_title?.text)
                    return (
                      <SummaryButton
                        disableRipple
                        onClick={() =>
                          document
                            .getElementById(section_title.text || '')
                            ?.scrollIntoView({ behavior: 'smooth' })
                        }
                        sx={{ textAlign: 'start', width: 'fit-content' }}
                      >
                        <Typography
                          key={section_summary_title.text || section_title.text}
                          sx={{ marginLeft: 1 }}
                        >
                          &bull;{' '}
                          {section_summary_title?.text
                            ? section_summary_title.text
                            : section_title.text}
                        </Typography>
                      </SummaryButton>
                    );
                  else return <Fragment />;
                })}
                {!isSmall && (
                  <>
                    {/*<Typography
                      color="primary"
                      component="h2"
                      fontSize="1.125rem"
                      sx={{ marginTop: 3 }}
                    >
                      <Trans i18nKey="geovelo.blog_post.other_articles" />
                    </Typography>
                    {relatedBlogPosts.map((data) => {
                      const blogPost = data?.related_blog_post?.document;
                      if (!blogPost || !('uid' in blogPost))
                        return <Fragment key={data?.related_blog_post?.uid} />;

                      return (
                        <SummaryButton
                          disableRipple
                          component={Link}
                          key={data?.related_blog_post?.uid}
                          sx={{ textAlign: 'start', width: 'fit-content' }}
                          to={blogPost?.url || '/'}
                        >
                          <Typography
                            sx={{ color: 'inherit', marginLeft: 1, textDecoration: 'none' }}
                          >
                            {blogPost.data.title.text}
                          </Typography>
                        </SummaryButton>
                      );
                    })}*/}
                    <ShareButtons
                      marginTop={3}
                      summary={subtitle?.text || undefined}
                      themes={themes}
                      title={`Article Geovelo - ${title?.text}`}
                      url={props.location.href}
                    />
                    <Box display="flex" justifyContent="center" marginTop={3}>
                      <CTA
                        color="secondary"
                        link={{
                          url: 'https://geovelo.app/fr/download/?utm_source=Blog',
                          target: 'blank',
                        }}
                        size="small"
                        title={{ text: t('geovelo.blog_post.discover') }}
                      />
                    </Box>
                  </>
                )}
              </Box>
            </Box>
          </Box>
        </Box>
        {relatedBlogPosts && relatedBlogPosts.length > 0 && (
          <Box
            display="flex"
            justifyContent="center"
            sx={{ backgroundColor: '#ecf2fb' }}
            width="100%"
          >
            <Box
              alignItems="center"
              display="flex"
              flexDirection="column"
              gap={5}
              maxWidth={1440}
              paddingX={{ xs: 4, md: 8, lg: 16 }}
              paddingY={8}
            >
              <Box alignItems="center" display="flex" justifyContent="space-between" width="100%">
                <Typography fontWeight={700}>
                  {prismicBlog?.data.related_blog_posts_label?.text ||
                    t('geovelo.blog_post.related_blog_posts')}
                </Typography>
                <Button component={Link} to={`/${currentLanguage}/blog`} variant="outlined">
                  <Trans i18nKey="geovelo.blog_post.all_articles" />
                </Button>
              </Box>
              <Box display="flex" flexWrap="wrap" gap={8}>
                {relatedBlogPosts.map((data) => {
                  const blogPost = data?.related_blog_post?.document;
                  if (!blogPost || !('uid' in blogPost))
                    return <Fragment key={data?.related_blog_post?.uid} />;

                  return (
                    <BlogPostCard
                      card
                      blogPost={blogPost}
                      key={data?.related_blog_post?.uid}
                      themes={allThemes}
                    />
                  );
                })}
              </Box>
            </Box>
          </Box>
        )}
      </Box>
    </>
  );
}

const SummaryButton = styled(ButtonBase)`
  transition: all 0.5s ease;

  &:hover {
    padding-left: 8px;
    color: rgba(0, 0, 0, 0.6);
  }
`;

export const query = graphql`
  fragment PrismicAuthor on PrismicAuthor {
    _previewable
    data {
      author_name {
        text
      }
      author_role {
        text
      }
      author_photo {
        gatsbyImageData(width: 256, placeholder: DOMINANT_COLOR)
        alt
      }
    }
  }
  fragment PrismicBlogPostAuthor on PrismicAuthorConnection {
    nodes {
      ...PrismicAuthor
    }
  }
  fragment BlogPostData on PrismicBlogPostData {
    themes {
      theme {
        uid
      }
    }
    title {
      text
    }
    meta_title {
      text
    }
    publication_date
    image {
      gatsbyImageData(width: 1000, placeholder: DOMINANT_COLOR)
      alt
      copyright
      url
    }
    subtitle {
      text
      html
    }
    subtitle_short {
      text
    }
    author {
      id
    }
    sections {
      image_order
      ride_order
      text_order
      insert_order
      iframe_order
      cta_order
      recap_ride_id
      recap_cta_title {
        text
      }
      recap_cta_link {
        link_type
        url
        slug
        target
      }
      section_title {
        text
      }
      section_summary_title {
        text
      }
      section_text {
        html
      }
      section_image {
        gatsbyImageData(width: 780, placeholder: DOMINANT_COLOR)
        dimensions {
          width
        }
        alt
        copyright
      }
      section_insert {
        html
      }
      section_cta_title {
        text
      }
      section_cta_link {
        link_type
        url
        slug
        target
      }
      section_iframe_link {
        link_type
        url
      }
      section_iframe_height
    }
    related_blog_posts {
      related_blog_post {
        uid
        document {
          ... on PrismicBlogPost {
            ...BlogPostPreview
          }
        }
      }
    }
  }
`;

export default withPrismicPreview(BlogPostPage);
